<template>
  <v-dialog v-model="$parent.fundsAndStocksDialog" fullscreen transition="dialog-bottom-transition">
    <v-card>
      <v-toolbar dark color="primary">
        <v-toolbar-title>Select Funds or Stocks that trigger the alert</v-toolbar-title>
        <div class="flex-grow-1" />
        <v-toolbar-items>
          <v-btn text dark class="ma-0" @click="$parent.fundsAndStocksDialog = false">
            Close
          </v-btn>
        </v-toolbar-items>
      </v-toolbar>

      <!-- Body -->
      <v-card-text>
        <v-radio-group v-model="alertCriteria.settings.filer_type_selection" mandatory>
          <v-radio :value="5" label="Receive an e-mail when changes to the WhaleIndex occur" />
          <v-radio :value="1" label="Receive an e-mail for all filers or a predefined group" />
          <v-radio :value="3" label="Select individual SEC filers" />
          <v-radio :value="4" label="Use one of your Stock Watchlists" />
        </v-radio-group>

        <!-- Receive an e-mail for all filers or a predefined group -->
        <template v-if="alertCriteria.settings.filer_type_selection === 1">
          <v-select
            v-model="alertCriteria.funds_and_stocks.predefined_id"
            item-title="text"
            :menu="true"
            :items="tags"
            variant="outlined"
            label="Choose a filer group"
            style="max-width: 400px"
          />
        </template>

        <!-- Select individual SEC filers -->
        <core-fund-group-manager
          v-if="alertCriteria.settings.filer_type_selection === 3"
          :filer-group="alertCriteria.funds_and_stocks.filer_group"
          text
          no-padding
          no-create-group
          @update:filer-group="
            newVal => {
              alertCriteria.funds_and_stocks.filer_group.id = newVal.id
              alertCriteria.funds_and_stocks.filer_group.filers = newVal.filers
            }
          "
        />

        <div v-if="alertCriteria.settings.filer_type_selection === 3">
          <v-alert
            v-if="limit"
            :value="true"
            type="info"
            class="d-inline-flex text-body-2 mt-0 mb-2"
          >
            {{ limit }} maximum number of funds allowed for your account.
          </v-alert>
          <v-alert
            v-if="limit && limit <= alertCriteria.funds_and_stocks.filer_group.filers.length"
            :type="
              limit === alertCriteria.funds_and_stocks.filer_group.filers.length
                ? 'warning'
                : 'error'
            "
            :value="true"
            class="d-inline-flex text-body-2 mt-0 mb-2"
          >
            You have
            {{
              limit === alertCriteria.funds_and_stocks.filer_group.filers.length
                ? 'reached'
                : 'exceeded'
            }}
            the maximum number of allowed group members
          </v-alert>
        </div>

        <!-- Track a basket of stocks -->
        <template v-if="alertCriteria.settings.filer_type_selection === 4">
          <helpers-section-header class="mt-0"> Stock Watchlist Selection </helpers-section-header>
          <p>
            When choosing one of your watchlists, you will receive an alert whenever a new 13F, Form
            3 or 4, or 13D/G (assuming you added those forms to this e-mail alert) filing is
            submitted that contains the stock. This will not send you an alert whenever the stock
            itself submits a filing.
          </p>
          <p>
            You will need to go to the
            <router-link to="/watchlists"> watchlist edit page </router-link>

            to create a new watchlist or edit your existing watchlists.
          </p>
          <v-container fluid pa-0 mb-12>
            <!-- Ticker search -->
            <v-row mt-6>
              <v-col cols="6">
                <v-select
                  v-model="alertCriteria.funds_and_stocks.stock_group"
                  :items="watchlists"
                  return-object
                  item-title="description"
                  item-value="id"
                  label="Choose a Watchlist"
                  variant="outlined"
                />
              </v-col>
              <!-- Add ticker -->
              <v-col v-if="1 == 2" class="shrink" cols="12" md="3" xl="2">
                <helpers-section-sub-header>Add ticker</helpers-section-sub-header>
                <v-row>
                  <v-col>
                    <v-autocomplete
                      v-model="alertCriteria.funds_and_stocks.stock_basket"
                      v-model:search-input="tickerSearchInput"
                      :items="tickerSearchResults"
                      :loading="loading"
                      :hide-no-data="!tickerSearchInput || loading"
                      no-data-text="No such ticker found"
                      return-object
                      item-title="yahoo_ticker_symbol"
                      item-value="id"
                      multiple
                      density="compact"
                      placeholder="Ticker symbol"
                      prepend-inner-icon="search"
                      append-icon=""
                      hide-details
                      variant="outlined"
                      class="pt-0"
                      style="width: 100%; max-width: 400px"
                    >
                      <template #selection></template>
                      <template #item="{ item }">
                        <v-list-item-title>{{ item.name }}</v-list-item-title>
                        <v-list-item-subtitle>{{ item.yahoo_ticker_symbol }}</v-list-item-subtitle>
                      </template>
                    </v-autocomplete>
                  </v-col>
                </v-row>
              </v-col>

              <!-- Your Stock Basket -->
              <v-col grow cols="12" md="9" xl="10">
                <helpers-section-sub-header>Stocks in your watchlist</helpers-section-sub-header>
                <v-row>
                  <v-col cols="12">
                    <span v-if="!alertCriteria.funds_and_stocks.stock_group"
                      >Please select your watchlist.</span
                    >
                    <v-chip
                      v-for="stock in alertCriteria.funds_and_stocks.stock_group.stocks"
                      v-else
                      :key="stock.id"
                      small
                      label
                      color="primary text-white"
                      style="margin: 2px"
                    >
                      <span class="font-weight-bold">{{ stock.ticker[0] }}:</span>&nbsp;
                      {{ stock.name }}
                    </v-chip>
                  </v-col>
                </v-row>
              </v-col>
            </v-row>

            <!-- Optional filters -->
            <v-row mt-6>
              <v-col cols="12">
                <helpers-section-sub-header>Optional Filters</helpers-section-sub-header>
                <p>These filters are used for 13F filings only.</p>
                <v-radio-group
                  v-model="alertCriteria.funds_and_stocks.stock_basket_action"
                  hide-details
                  class="v-radio-group--x-small"
                >
                  <v-radio :value="1" label="Receive alert for any activity in stock basket" />
                  <v-radio :value="2" label="When shares are purchased" />
                  <v-radio :value="3" label="When shares are sold" />
                  <v-radio :value="4" label="When there is a 25% or greater change in shares" />
                  <v-radio :value="5" label="When there is a 50% or greater change in shares" />
                  <v-radio :value="6" label="When there is a 75% or greater change in shares" />
                </v-radio-group>
                <v-checkbox
                  v-model="stock_basket_filer_restriction"
                  label="Restrict your stock basket to a group of funds"
                  hint="If one of the funds in the group below submits a 13F or 13D/G that contains one of the securities in your basket of stocks above, you will receive an alert"
                  persistent-hint
                  class="mt-6 mb-2"
                  @click="UpdateFilerRestriction()"
                />
              </v-col>
              <v-col v-if="stock_basket_filer_restriction" cols="12">
                <core-fund-group-manager
                  :filer-group="alertCriteria.funds_and_stocks.filer_group"
                  text
                  no-padding
                  no-create-group
                  @update:filer-group="
                    newVal => {
                      alertCriteria.funds_and_stocks.filer_group.id = newVal.id
                      alertCriteria.funds_and_stocks.filer_group.filers = newVal.filers
                    }
                  "
                />
              </v-col>
            </v-row>
          </v-container>
        </template>

        <!-- Receive an e-mail when changes to the WhaleIndex occur -->
        <v-col v-if="alertCriteria.settings.filer_type_selection === 5">
          <helpers-section-header class="mt-0">
            Choose WhaleIndices to receive an alert for:
          </helpers-section-header>
          <v-checkbox
            v-for="item in whaleindices"
            :key="item.id"
            :model-value="
              alertCriteria.funds_and_stocks.whaleindices.some(indice => indice.id === item.id)
            "
            :label="item.description"
            hide-details
            color="primary"
            class="mt-1"
            @update:model-value="toggleWhaleIndice($event, item)"
          />
        </v-col>
      </v-card-text>
    </v-card>
  </v-dialog>
</template>

<script>
// Modules
import api from '@/store/api'
import { mapWritableState } from 'pinia'
import { mapStoreState } from '@/store/helpers'
import { useAppStore, useUserStore } from '@/store'
// Static data
import { tags } from '@/data/constants'

export default {
  data: () => ({
    loading: false,
    tickerSearchInput: null,
    tickerSearchResults: [],
    whaleindices: [],
    stock_basket_filer_restriction: false,
    tags
  }),

  computed: {
    ...mapWritableState(useAppStore, ['alertCriteria']),
    ...mapStoreState('user', ['watchlists']),
    ...mapStoreState({
      limit: state => state.user.limit
    })
  },

  watch: {
    tickerSearchInput(val) {
      val && this.queryTickerSearch(val)
      this.tickerSearchInput = null
    },

    'alertCriteria.settings.filer_type_selection'(val) {
      switch (val) {
        // predefined tags
        case 1:
          this.alertCriteria.funds_and_stocks.filer_group = {
            id: null,
            filers: [],
            description: ''
          }
          this.alertCriteria.funds_and_stocks.stock_basket = []
          this.alertCriteria.funds_and_stocks.stock_basket_action = 1
          this.alertCriteria.funds_and_stocks.whaleindices = []
          this.alertCriteria.funds_and_stocks.stock_group = {
            id: null,
            stocks: [],
            description: ''
          }
          break
        // filers
        case 3:
          this.alertCriteria.funds_and_stocks.predefined_id = null
          this.alertCriteria.funds_and_stocks.stock_basket = []
          this.alertCriteria.funds_and_stocks.stock_basket_action = 1
          this.alertCriteria.funds_and_stocks.whaleindices = []
          this.alertCriteria.funds_and_stocks.stock_group = {
            id: null,
            stocks: [],
            description: ''
          }
          break
        // stock basket
        case 4:
          this.alertCriteria.funds_and_stocks.predefined_id = null
          this.alertCriteria.funds_and_stocks.filer_group = {
            id: null,
            filers: [],
            description: ''
          }
          this.alertCriteria.funds_and_stocks.whaleindices = []
          break
        // whaleindices
        case 5:
          this.alertCriteria.funds_and_stocks.predefined_id = null
          this.alertCriteria.funds_and_stocks.filer_group = {
            id: null,
            filers: [],
            description: ''
          }
          this.alertCriteria.funds_and_stocks.stock_basket = []
          this.alertCriteria.funds_and_stocks.stock_basket_action = 1
          this.alertCriteria.settings.form_type_selection = 'all'
          this.alertCriteria.form_types = []
          this.alertCriteria.funds_and_stocks.stock_group = {
            id: null,
            stocks: [],
            description: ''
          }
          this.alertCriteria.form8k_events = []
      }
    }
  },

  mounted() {
    // if (!this.alertCriteria.funds_and_stocks.stock_group)
    api
      .get('whaleindex.json')
      .then(response => {
        this.whaleindices = response.data.map(item => item.whaleindex)
      })
      .catch(error => console.log(error))
    // if Stock Basket is used, any selected filers are considered restricting
    if (
      this.alertCriteria.funds_and_stocks.stock_group.stocks.length > 0 &&
      this.alertCriteria.funds_and_stocks.filer_group.filers.length > 0
    ) {
      this.stock_basket_filer_restriction = true
    }
  },

  created() {},

  methods: {
    UpdateFilerRestriction() {
      if (!this.stock_basket_filer_restriction) {
        this.alertCriteria.funds_and_stocks.filer_group = { id: null, filers: [] }
      }
    },
    queryTickerSearch(val) {
      this.loading = true

      api
        .get(`stock/${this.tickerSearchInput}.json?isArray=true`)
        .then(res => {
          this.tickerSearchResults = res.data
        })
        .then(() => (this.loading = false))
    },

    removeStockById(id) {
      const index = this.alertCriteria.funds_and_stocks.stock_basket.findIndex(
        stock => stock.id === id
      )
      this.alertCriteria.funds_and_stocks.stock_basket.splice(index, 1)
    },

    toggleWhaleIndice(isEnabled, whaleindice) {
      if (isEnabled) {
        this.alertCriteria.funds_and_stocks.whaleindices.push(whaleindice)
      } else {
        this.alertCriteria.funds_and_stocks.whaleindices =
          this.alertCriteria.funds_and_stocks.whaleindices.filter(
            item => item.id !== whaleindice.id
          )
      }
    }
  }
}
</script>
